import React from 'react'
import { inject, observer } from 'mobx-react'
import Icon from 'components/common/Icon'
import { trackValue } from 'utils.js'
import ColorPicker from '../common/ColorPicker'
import NumberInput from 'components/common/NumberInput'
import MapInstruction from '../common/MapInstruction'
import _ from 'lodash'
import { withTranslation } from 'react-i18next'
import { placeAMarker } from 'constants.js'
import PaneHeader from './PaneHeader'
import LocationCoordsField from './LocationCoordsField'

const MarkerPane = ({
  marker, group, uiStore, dataStore, selectionStore, edit, onCancel, onSave, isQuickCreate, t
}) => {
  const handleConfirmAdd = async () => {
    if (!marker.validate(group)) {
      return
    }
    if (onSave) {
      onSave()
    } else if (uiStore.quickCreate && uiStore.quickCreate.isOpen) {
      uiStore.closeQuickCreate()
    }
    // Markers save immediately and get added to general bucket
    await uiStore.addMarker(group, marker)
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
      return
    }
    if (uiStore.quickCreate && uiStore.quickCreate.isOpen) {
      uiStore.closeQuickCreate()
      return
    }
    if (edit) {
      selectionStore.select(_.find(group.savedMarkers, { id: marker.id }), { data: { group } })
    } else {
      selectionStore.clear()
    }
  }

  const paneTitle = edit
    ? t('ITEM.EDIT_MARKER', { defaultValue: 'Edit marker' })
    : (isQuickCreate ? 'Save point' : t('ITEM.MARKER_TITLE', { defaultValue: 'Marker' }))

  const submitBtnText = edit
    ? t('GLOBAL.CONFIRM_SAVE', { defaultValue: 'Save marker' })
    : t('ITEM.CREATE_MARKER_BTN', { defaultValue: 'Create marker' })

  if (isQuickCreate) {
    return (
      <div className='line-pane'>
        <section>
          <h2>{paneTitle}</h2>
        </section>
        <section className='line-pane__instruction'>
          {
            marker.position.latLng || edit
              ? null
              : <MapInstruction>{t('ITEM.MARKER_START')}</MapInstruction>
          }
        </section>
        <form>
          <section className='spaced-children'>
            <label>
              <span>{t('GLOBAL.NAME')}</span>
              <input
                className='name-input' type='text'
                {...trackValue(marker, 'name')} autoFocus
              />
              <span className='invalid'>{marker.invalid.get('name')}</span>
            </label>
            <label>
              <span>{t('GLOBAL.LONGITUDE')}</span>
              <NumberInput
                value={marker.position.longitude}
                min={-180}
                max={180}
                inputValue={marker.position.longitude}
                onChange={value => (marker.position.longitude = value)}
              />
              <span className='invalid'>{marker.invalid.get('longitude')}</span>
            </label>
            <label>
              <span>{t('GLOBAL.LATITUDE')}</span>
              <NumberInput
                min={-90}
                max={90}
                inputValue={marker.position.latitude}
                value={marker.position.latitude}
                onChange={value => (marker.position.latitude = value)}
              />
              <span className='invalid'>{marker.invalid.get('latitude')}</span>
            </label>
            <div>
              <ColorPicker
                onClick={(idx) => { marker.color_id = idx }}
                selectedId={marker.color_id} hasTransparencyOption={false}
              />
            </div>
            <label>
              <span>{t('GLOBAL.DESCRIPTION')}</span>
              <textarea
                spellCheck={false} className='description-input'
                {...trackValue(marker, 'description')} autoFocus
              />
              <span className='invalid'>{marker.invalid.get('description')}</span>
            </label>
          </section>
          <section>
            <div className='centered-button-row'>
              <button
                type='button'
                onClick={handleCancel}
              >
                <Icon id='fa-times' />
                <span>{t('GLOBAL.CANCEL')}</span>
              </button>
              <button
                type='button'
                className='ok'
                onClick={() => (edit
                  ? uiStore.editMarker(group, marker)
                  : handleConfirmAdd()
                )}
              >
                <Icon id='fa-check' />
                <span>{edit ? t('GLOBAL.CONFIRM_SAVE') : t('GLOBAL.CONFIRM_ADD')}</span>
              </button>
            </div>
          </section>
        </form>
      </div>
    )
  }

  return (
    <div className='line-pane'>
      <PaneHeader
        icon={placeAMarker}
        title={paneTitle}
        onClose={handleCancel}
      />

      {(!edit && (!marker.position.latitude || !marker.position.longitude)) && (
        <MapInstruction>{t('ITEM.MARKER_START', { defaultValue: 'Click on the map to place the marker' })}</MapInstruction>
      )}

      <form onSubmit={(e) => {
        e.preventDefault()
        if (edit) {
          uiStore.editMarker(group, marker)
        } else {
          handleConfirmAdd()
        }
      }}
      >
        <section className='spaced-children'>
          <label>
            <span>{t('GLOBAL.NAME', { defaultValue: 'Name' })}</span>
            <input
              className='name-input'
              type='text'
              placeholder={t('ITEM.MARKER_NAME_PLACEHOLDER', { defaultValue: 'e.g. Branch location' })}
              {...trackValue(marker, 'name')}
              autoFocus
            />
            <span className='invalid'>{marker.invalid.get('name')}</span>
          </label>

          <LocationCoordsField
            position={marker.position}
            onLatitudeChange={value => (marker.position.latitude = value)}
            onLongitudeChange={value => (marker.position.longitude = value)}
            invalidLatitude={marker.invalid.get('latitude')}
            invalidLongitude={marker.invalid.get('longitude')}
            t={t}
          />

          <div>
            <ColorPicker
              onClick={(idx) => { marker.color_id = idx }}
              selectedId={marker.color_id}
              hasTransparencyOption={false}
            />
          </div>

          <label>
            <span>{t('GLOBAL.DESCRIPTION', { defaultValue: 'Description' })}</span>
            <textarea
              spellCheck={false}
              className='description-input'
              placeholder={t('ITEM.MARKER_DESC_PLACEHOLDER', { defaultValue: 'e.g. Additional notes...' })}
              {...trackValue(marker, 'description')}
            />
            <span className='invalid'>{marker.invalid.get('description')}</span>
          </label>
        </section>

        <section>
          <div className='centered-button-row'>
            <button
              type='button'
              onClick={handleCancel}
            >
              <span>{t('GLOBAL.CANCEL', { defaultValue: 'Cancel' })}</span>
            </button>
            <button
              type='button'
              className='ok'
              onClick={() => (edit
                ? uiStore.editMarker(group, marker)
                : handleConfirmAdd()
              )}
            >
              <span>{submitBtnText}</span>
            </button>
          </div>
        </section>
      </form>
    </div>
  )
}

export default withTranslation()(inject('uiStore', 'dataStore', 'selectionStore')(observer(MarkerPane)))
